import { maleDemographicData } from '@/data/maleDemographicData';

export interface MaleFormData {
  ageRange: [number, number];
  race: string[];
  heightMin: string;
  incomeMin: string;
  politics: string;
  maritalStatus: string;
  bodyType: string;
  educationalLevel: string;
  employment: string;
}

export interface MaleCalculationResult {
  probability: number;
  absoluteNumbers: {
    compatible: number;
    total: number;
  };
  breakdown: {
    factor: string;
    percentage: number;
  }[];
}

// Helper function to get age group from age range
function getAgeGroup(ageRange: [number, number]): string {
  const avgAge = (ageRange[0] + ageRange[1]) / 2;
  if (avgAge <= 24) return '18-24';
  if (avgAge <= 34) return '25-34';
  return '35-44';
}

// Helper function to map race categories to data keys
function mapRaceToDataKey(race: string): string {
  const raceMapping: Record<string, string> = {
    'all': 'all',
    'white': 'white',
    'black': 'black',
    'hispanic': 'hispanic',
    'asian': 'asian',
    'native-american': 'other',
    'middle-eastern': 'other',
    'pacific-islander': 'other',
    'multiracial': 'other',
    'other': 'other'
  };
  return raceMapping[race] || 'other';
}

// Helper function to calculate age range probability with multiple races
function calculateAgeRangeProbability(ageRange: [number, number], races: string[]): number {
  if (races.includes('all')) {
    return calculateAgeRangeProbabilityForRace(ageRange, 'all');
  }

  // Calculate weighted average for multiple races
  let totalProb = 0;
  let totalWeight = 0;

  races.forEach(race => {
    const mappedRace = mapRaceToDataKey(race);
    const raceWeight = maleDemographicData.population.by_race[mappedRace] || 0;
    if (raceWeight > 0) {
      const raceProb = calculateAgeRangeProbabilityForRace(ageRange, mappedRace);
      totalProb += raceProb * raceWeight;
      totalWeight += raceWeight;
    }
  });

  return totalWeight > 0 ? totalProb / totalWeight : 0;
}

// Helper function to calculate age range probability for a single race
function calculateAgeRangeProbabilityForRace(ageRange: [number, number], race: string): number {
  const mappedRace = mapRaceToDataKey(race);
  const ageData = maleDemographicData.age_distribution[mappedRace];
  if (!ageData) return 0;

  // Calculate the proportion of men in the selected age range
  // using the age group distributions as weights
  let totalProportion = 0;

  // Define age group boundaries
  const ageGroups = [
    { key: '18-24', min: 18, max: 24, weight: ageData['18-24'] },
    { key: '25-34', min: 25, max: 34, weight: ageData['25-34'] },
    { key: '35-44', min: 35, max: 44, weight: ageData['35-44'] }
  ];

  // For each age group, calculate how much of it overlaps with the selected range
  ageGroups.forEach(group => {
    const overlapStart = Math.max(group.min, ageRange[0]);
    const overlapEnd = Math.min(group.max, ageRange[1]);

    if (overlapStart <= overlapEnd) {
      // Calculate what fraction of this age group is included
      const groupSpan = group.max - group.min + 1;
      const overlapSpan = overlapEnd - overlapStart + 1;
      const fractionIncluded = overlapSpan / groupSpan;

      // Add the weighted proportion
      totalProportion += group.weight * fractionIncluded;
    }
  });

  return Math.min(totalProportion, 1.0);
}

// Helper function to calculate age weights across the three age groups
function calculateAgeWeights(ageRange: [number, number]): Record<string, number> {
  const ageGroups = ['18-24', '25-34', '35-44'];
  const weights: Record<string, number> = {};

  ageGroups.forEach(group => {
    const [minAge, maxAge] = group.split('-').map(Number);
    const overlap = Math.max(0, Math.min(ageRange[1], maxAge) - Math.max(ageRange[0], minAge) + 1);
    const totalRange = ageRange[1] - ageRange[0] + 1;
    weights[group] = overlap / totalRange;
  });

  return weights;
}

// Helper function to convert height keys like '5-10' into display labels like 5'10"
function formatHeightLabel(heightKey: string): string {
  const [feet, inches] = heightKey.split('-');
  return `${feet}'${inches}"`;
}

// Helper function to convert income keys like '100k' into display labels like $100,000+
function formatIncomeLabel(incomeKey: string): string {
  const thousands = parseInt(incomeKey.replace('k', ''), 10);
  return `$${(thousands * 1000).toLocaleString('en-US')}+`;
}

export function calculateMaleCompatibility(formData: MaleFormData): MaleCalculationResult {
  const breakdown: { factor: string; percentage: number }[] = [];
  let totalProbability = 1.0;

  // Base population by race(s)
  let raceProb = 1.0;
  if (!formData.race.includes('all')) {
    // Sum probabilities for selected races using mapped keys
    raceProb = formData.race.reduce((sum, race) => {
      const mappedRace = mapRaceToDataKey(race);
      return sum + (maleDemographicData.population.by_race[mappedRace] || 0);
    }, 0);

    const raceLabels = formData.race.map(race => {
      const labels: Record<string, string> = {
        white: 'White (Non-Hispanic)',
        black: 'Black or African American',
        hispanic: 'Hispanic or Latino',
        asian: 'Asian',
        'native-american': 'Native American or Alaska Native',
        'middle-eastern': 'Middle Eastern or North African',
        'pacific-islander': 'Pacific Islander',
        multiracial: 'Multiracial/Mixed Race',
        other: 'Other'
      };
      return labels[race] || race;
    });

    breakdown.push({
      factor: `Race/Ethnicity: ${raceLabels.join(', ')}`,
      percentage: raceProb * 100
    });
  }
  totalProbability *= raceProb;

  // Age range probability
  const ageProb = calculateAgeRangeProbability(formData.ageRange, formData.race);
  breakdown.push({
    factor: `Age Range: ${formData.ageRange[0]}-${formData.ageRange[1]}`,
    percentage: ageProb * 100
  });
  totalProbability *= ageProb;

  // Height - cumulative share at or above threshold (race-independent, NHANES)
  if (formData.heightMin !== 'no-preference') {
    const heightProb = maleDemographicData.height_at_least[formData.heightMin] || 1.0;

    breakdown.push({
      factor: `Height: ${formatHeightLabel(formData.heightMin)} or taller`,
      percentage: heightProb * 100
    });
    totalProbability *= heightProb;
  }

  // Income - age-weighted share earning at least the selected threshold
  if (formData.incomeMin !== 'no-preference') {
    const incomeData = maleDemographicData.income_at_least_by_age[formData.incomeMin];
    let incomeProb = 0;

    if (incomeData) {
      const ageWeights = calculateAgeWeights(formData.ageRange);
      Object.entries(ageWeights).forEach(([ageGroup, weight]) => {
        if (weight > 0) {
          incomeProb += (incomeData[ageGroup] || 0) * weight;
        }
      });
    }

    breakdown.push({
      factor: `Income: ${formatIncomeLabel(formData.incomeMin)}`,
      percentage: incomeProb * 100
    });
    totalProbability *= incomeProb;
  }

  // Political affiliation - use weighted average for multiple races
  if (formData.politics !== 'no-preference') {
    let politicsProb = 0;

    if (formData.race.includes('all')) {
      const politicsData = maleDemographicData.political_affiliation.all;
      politicsProb = politicsData[formData.politics] || 0.33;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const politicsData = maleDemographicData.political_affiliation[mappedRace];
        const raceWeight = maleDemographicData.population.by_race[mappedRace] || 0;
        if (politicsData) {
          politicsProb += (politicsData[formData.politics] || 0.33) * raceWeight;
          totalWeight += raceWeight;
        }
      });
      politicsProb = totalWeight > 0 ? politicsProb / totalWeight : 0.33;
    }

    breakdown.push({
      factor: `Political Leaning: ${formData.politics}`,
      percentage: politicsProb * 100
    });
    totalProbability *= politicsProb;
  }

  // Marital status - use weighted average for multiple races
  if (formData.maritalStatus !== 'no-preference') {
    const ageGroup = getAgeGroup(formData.ageRange);
    let maritalProb = 0;

    if (formData.race.includes('all')) {
      const maritalData = maleDemographicData.marital_status.all;
      if (formData.maritalStatus === 'never-married') {
        maritalProb = maritalData.never_married[ageGroup] || 0.5;
      } else if (formData.maritalStatus === 'divorced-separated') {
        maritalProb = maritalData.divorced_separated[ageGroup] || 0.15;
      }
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const maritalData = maleDemographicData.marital_status[mappedRace];
        const raceWeight = maleDemographicData.population.by_race[mappedRace] || 0;
        if (maritalData) {
          let raceMaritalProb = 0;
          if (formData.maritalStatus === 'never-married') {
            raceMaritalProb = maritalData.never_married[ageGroup] || 0.5;
          } else if (formData.maritalStatus === 'divorced-separated') {
            raceMaritalProb = maritalData.divorced_separated[ageGroup] || 0.15;
          }
          maritalProb += raceMaritalProb * raceWeight;
          totalWeight += raceWeight;
        }
      });
      maritalProb = totalWeight > 0 ? maritalProb / totalWeight : 0.5;
    }

    const statusLabel = formData.maritalStatus === 'never-married' ? 'Never Married' : 'Divorced/Separated';
    breakdown.push({
      factor: `Marital Status: ${statusLabel}`,
      percentage: maritalProb * 100
    });
    totalProbability *= maritalProb;
  }

  // Body type (BMI) - use weighted average for multiple races
  if (formData.bodyType !== 'no-preference') {
    let notObeseProb = 0;

    if (formData.race.includes('all')) {
      const bmiData = maleDemographicData.bmi_distribution.all;
      notObeseProb = bmiData.not_obese || 0.60;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const bmiData = maleDemographicData.bmi_distribution[mappedRace];
        const raceWeight = maleDemographicData.population.by_race[mappedRace] || 0;
        if (bmiData) {
          notObeseProb += (bmiData.not_obese || 0.60) * raceWeight;
          totalWeight += raceWeight;
        }
      });
      notObeseProb = totalWeight > 0 ? notObeseProb / totalWeight : 0.60;
    }

    breakdown.push({
      factor: 'Not Obese (BMI < 30)',
      percentage: notObeseProb * 100
    });
    totalProbability *= notObeseProb;
  }

  // Educational level - use age and race-weighted calculation
  if (formData.educationalLevel !== 'no-preference') {
    const ageGroup = getAgeGroup(formData.ageRange);
    let educationProb = 0;

    if (formData.race.includes('all')) {
      const educationData = maleDemographicData.educational_attainment.all;
      const levelData = educationData[formData.educationalLevel as keyof typeof educationData];
      educationProb = levelData ? (levelData[ageGroup] || 0.25) : 0.25;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const educationData = maleDemographicData.educational_attainment[mappedRace];
        const raceWeight = maleDemographicData.population.by_race[mappedRace] || 0;
        if (educationData) {
          const levelData = educationData[formData.educationalLevel as keyof typeof educationData];
          const raceEducationProb = levelData ? (levelData[ageGroup] || 0.25) : 0.25;
          educationProb += raceEducationProb * raceWeight;
          totalWeight += raceWeight;
        }
      });
      educationProb = totalWeight > 0 ? educationProb / totalWeight : 0.25;
    }

    const educationLabels: Record<string, string> = {
      'high_school_or_less': 'High School or Less',
      'some_college': 'Some College',
      'bachelors_degree': "Bachelor's Degree",
      'graduate_degree': 'Graduate Degree'
    };

    breakdown.push({
      factor: `Education: ${educationLabels[formData.educationalLevel] || formData.educationalLevel}`,
      percentage: educationProb * 100
    });
    totalProbability *= educationProb;
  }

  // Employment - age-weighted share of men employed full- or part-time
  if (formData.employment !== 'no-preference') {
    const ageWeights = calculateAgeWeights(formData.ageRange);
    let employmentProb = 0;

    Object.entries(ageWeights).forEach(([ageGroup, weight]) => {
      if (weight > 0) {
        employmentProb += (maleDemographicData.employment.employed_by_age[ageGroup] || 0) * weight;
      }
    });

    breakdown.push({
      factor: 'Employment: Employed',
      percentage: employmentProb * 100
    });
    totalProbability *= employmentProb;
  }

  // Calculate absolute numbers
  const basePopulation = maleDemographicData.population.total_men_18_44;
  const compatibleMen = Math.round(basePopulation * totalProbability);

  return {
    probability: totalProbability * 100,
    absoluteNumbers: {
      compatible: compatibleMen,
      total: basePopulation
    },
    breakdown
  };
}
